import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Phone, MessageCircle, Maximize2, Building2, Star, Heart, Share2, ChevronLeft, ChevronRight, Eye, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { formatPrice } from '../data/mockData';
import { toast } from 'sonner';
import { useState, useEffect, useRef, useCallback } from 'react';
import { ListingCard } from '../components/ListingCard';
import { supabase } from '../lib/supabase';

export default function ListingDetail({ listings, agencies }) {
  // ============================================
  // ALL HOOKS MUST BE AT THE TOP (Netlify Build Fix)
  // ============================================
  const { id } = useParams();
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(() => {
    const saved = localStorage.getItem('mydom_favorites');
    return saved ? JSON.parse(saved).includes(id) : false;
  });
  const [activeImage, setActiveImage] = useState(0);
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('mydom_favorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [viewCount, setViewCount] = useState(null);
  const hasIncrementedViews = useRef(null);
  const contactRef = useRef(null);
  const descriptionRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  // Find listing - this can be null
  const listing = listings.find(l => l.id === id);

  // Sticky sidebar kontrolü
  useEffect(() => {
    const handleScroll = () => {
      if (!contactRef.current || !descriptionRef.current) return;

      const contactElement = contactRef.current;
      const descriptionElement = descriptionRef.current;
      const descriptionSection = document.getElementById('description-section');
      
      if (!descriptionSection) return;
      
      const descriptionBottom = descriptionSection.getBoundingClientRect().bottom + window.scrollY;
      const windowHeight = window.innerHeight;
      const scrollY = window.scrollY;
      
      // Səhifə aşağı sürüşdükdə sticky ol
      if (scrollY > 200) {
        setIsSticky(true);
        
        // Təsvir bölməsi bitəndə sticky dayandır
        if (descriptionBottom < scrollY + windowHeight) {
          // Təsvir bölməsi bitib, sticky dayandır - absolute pozisiya
          contactElement.style.position = 'absolute';
          contactElement.style.top = `${descriptionBottom - contactElement.offsetHeight - 32}px`;
          contactElement.style.bottom = 'auto';
        } else {
          // Normal sticky rejim
          contactElement.style.position = 'sticky';
          contactElement.style.top = '120px';
          contactElement.style.bottom = 'auto';
        }
      } else {
        setIsSticky(false);
        contactElement.style.position = 'relative';
        contactElement.style.top = 'auto';
        contactElement.style.bottom = 'auto';
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // İlk yükləmədə də yoxla
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [listing]);

  // Initialize view count from listing
  useEffect(() => {
    if (listing && viewCount === null) {
      setViewCount(listing.views ?? 0);
    }
  }, [listing, viewCount]);

  // Main useEffect: Handle favorites and view counting
  useEffect(() => {
    const saved = localStorage.getItem('mydom_favorites');
    if (saved) {
      const savedFavorites = JSON.parse(saved);
      setFavorites(savedFavorites);
      setIsFavorite(savedFavorites.includes(id));
    }

    // Increment view count when page loads (only once per listing)
    if (hasIncrementedViews.current !== id && id) {
      hasIncrementedViews.current = id;

      const incrementViews = async () => {
        try {
          // The RPC function now RETURNS the new count directly
          const { data: newCount, error } = await supabase.rpc('increment_views', { listing_id: id });

          if (error) {
            console.error('Error incrementing views:', error);
            return;
          }

          // Update local state with the returned count
          if (newCount !== null && newCount !== undefined) {
            setViewCount(newCount);
          }
        } catch (err) {
          console.error('Error in incrementViews:', err);
        }
      };

      incrementViews();
    }
  }, [id]);

  // ============================================
  // CONDITIONAL RETURN AFTER ALL HOOKS
  // ============================================
  if (!listing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Elan tapılmadı</p>
          <Link to="/" className="text-[#B91C1C]">Ana səhifəyə qayıt</Link>
        </div>
      </div>
    );
  }

  // ============================================
  // COMPUTED VALUES & HANDLERS
  // ============================================
  const agency = listing.agencyId ? agencies.find(a => a.id === listing.agencyId) : null;
  const images = Array.isArray(listing.images) ? listing.images : [listing.image];

  // Navigation items
  const navItems = [
    { label: 'Alqı-satqı', href: '/?category=sale' },
    { label: 'Kirayə', href: '/?category=rent' },
    { label: 'Gündəlik Kirayə', href: '/?category=daily' },
    { label: 'Agentliklər', href: '/agencies' },
    { label: 'Yaşayış Kompleksləri', href: '/complexes' },
  ];

  // Generate dynamic page title
  const getPageTitle = () => {
    const location = listing.location;
    const type = listing.type;
    const price = formatPrice(listing.price, listing.category);
    return `${location} ${type} ${price}`;
  };

  // Find similar listings - ana səhifədəki kimi bütün uyğun elanlar
  const getSimilarListings = () => {
    return listings.filter(l => 
      l.id !== listing.id && (
        l.category === listing.category ||
        l.location === listing.location ||
        l.type === listing.type
      )
    );
  };

  const similarListings = getSimilarListings();

  // Get view count (use local state if available, otherwise fallback to listing.views)
  const getViewCount = () => {
    if (viewCount !== null) {
      return viewCount;
    }
    return listing.views ?? 0;
  };

  // Format date (DD.MM.YYYY, HH:MM)
  const getFormattedDate = () => {
    let dateToFormat;
    if (listing.created_at) {
      dateToFormat = new Date(listing.created_at);
    } else if (listing.createdAt) {
      dateToFormat = new Date(listing.createdAt);
    } else {
      dateToFormat = new Date();
    }

    const day = String(dateToFormat.getDate()).padStart(2, '0');
    const month = String(dateToFormat.getMonth() + 1).padStart(2, '0');
    const year = dateToFormat.getFullYear();
    const hours = String(dateToFormat.getHours()).padStart(2, '0');
    const minutes = String(dateToFormat.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  // Image carousel navigation
  const handlePreviousImage = () => {
    setActiveImage((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setActiveImage((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Google Maps link handler
  const handleMapClick = () => {
    const query = encodeURIComponent(listing.location);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  const handleCall = () => window.location.href = `tel:${listing.phone}`;
  
  const handleWhatsApp = () => {
    if (!listing.phone) {
      toast.error('Telefon nömrəsi mövcud deyil');
      return;
    }
    const phoneNumber = listing.phone.replace(/[^0-9]/g, ''); // Yalnız rəqəmləri saxla
    const message = encodeURIComponent(`Salam! ${listing.location} ünvanındakı elan ilə maraqlanıram.`);
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank', 'noopener,noreferrer');
  };

  const handleFavorite = () => {
    const newFavorites = favorites.includes(id) 
      ? favorites.filter(f => f !== id) 
      : [...favorites, id];
    localStorage.setItem('mydom_favorites', JSON.stringify(newFavorites));
    setFavorites(newFavorites);
    setIsFavorite(!isFavorite);
    toast.success(isFavorite ? 'Seçilmişlərdən silindi' : 'Seçilmişlərə əlavə edildi');
  };

  const handleShare = async () => {
    try {
      await navigator.share({ title: `${listing.type} - ${listing.location}`, url: window.location.href });
    } catch {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link kopyalandı');
    }
  };

  const handleFavoriteToggle = (listingId) => {
    setFavorites(prev => {
      const newFavorites = prev.includes(listingId) 
        ? prev.filter(f => f !== listingId) 
        : [...prev, listingId];
      localStorage.setItem('mydom_favorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="min-h-screen bg-gray-50" data-testid="listing-detail-page">
      {/* Top Navigation Bar */}
      <div className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center justify-center gap-4 md:gap-6 flex-wrap">
            {navItems.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className="text-sm font-medium text-gray-700 hover:text-[#B91C1C] transition-colors whitespace-nowrap"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Header with back button and actions */}
      <header className="sticky top-[49px] z-40 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-[#B91C1C]">
            <ArrowLeft size={20} /><span>Geri</span>
          </button>
          <Link to="/" className="logo-text text-2xl font-semibold text-[#B91C1C]">Mydom</Link>
          <div className="flex items-center gap-2">
            <button onClick={handleFavorite} className="p-2 rounded-lg hover:bg-gray-100">
              <Heart size={20} className={isFavorite ? 'text-[#B91C1C] fill-[#B91C1C]' : 'text-gray-400'} />
            </button>
            <button onClick={handleShare} className="p-2 rounded-lg hover:bg-gray-100">
              <Share2 size={20} className="text-gray-400" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Dynamic Page Title */}
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 logo-text">
          {getPageTitle()}
        </h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div ref={descriptionRef} className="lg:col-span-2 space-y-6">
            {/* Images with Carousel Arrows */}
            <div className="space-y-4">
              <div className="relative rounded-2xl overflow-hidden aspect-video group">
                <img src={images[activeImage]} alt={listing.location} className="w-full h-full object-cover" />
                
                {/* Left Arrow */}
                {images.length > 1 && (
                  <button
                    onClick={handlePreviousImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                    aria-label="Previous image"
                  >
                    <ChevronLeft size={24} className="text-gray-700" />
                  </button>
                )}

                {/* Right Arrow */}
                {images.length > 1 && (
                  <button
                    onClick={handleNextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/90 hover:bg-white rounded-full shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                    aria-label="Next image"
                  >
                    <ChevronRight size={24} className="text-gray-700" />
                  </button>
                )}

                <div className="absolute top-4 left-4 flex gap-2">
                  {listing.isVip && <span className="vip-badge flex items-center gap-1"><Star size={12} fill="currentColor" />VIP</span>}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    listing.category === 'rent' ? 'bg-blue-500 text-white' : 
                    listing.category === 'daily' ? 'bg-purple-500 text-white' : 'bg-green-500 text-white'
                  }`}>
                    {listing.category === 'rent' ? 'Kirayə' : listing.category === 'daily' ? 'Gündəlik Kirayə' : 'Satılır'}
                  </span>
                  {listing.ownerType === 'sahibinden' && (
                    <span className="px-3 py-1 bg-green-500 text-white text-sm rounded-full">Sahibindən</span>
                  )}
                </div>
              </div>

              {/* Statistics Bar */}
              <div className="bg-white rounded-xl p-4 border shadow-sm">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  {/* View Count */}
                  <div className="flex items-center gap-2 text-gray-700">
                    <Eye size={18} className="text-[#B91C1C]" />
                    <span className="text-sm font-medium">
                      Baxışların sayı: <span className="font-semibold text-gray-900">{getViewCount()}</span>
                    </span>
                  </div>

                  {/* Date */}
                  <div className="flex items-center gap-2 text-gray-700">
                    <Clock size={18} className="text-[#B91C1C]" />
                    <span className="text-sm font-medium">
                      Yeniləndi: <span className="font-semibold text-gray-900">{getFormattedDate()}</span>
                    </span>
                  </div>
                </div>
              </div>

              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {images.map((img, i) => (
                    <button key={i} onClick={() => setActiveImage(i)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${activeImage === i ? 'border-[#B91C1C]' : 'border-transparent'}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Price & Location */}
            <div className="bg-white rounded-xl p-6 border">
              {/* Emoji Xüsusiyyətlər */}
              {(listing.tamir_status || listing.ipoteka || listing.cixaris) && (
                <div className="flex flex-wrap gap-3 mb-4 p-4 bg-gray-50 rounded-lg border">
                  {listing.tamir_status && (
                    <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <span className="text-lg">🏠</span>
                      <span className="capitalize">{listing.tamir_status}</span>
                    </span>
                  )}
                  {listing.ipoteka && (
                    <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <span className="text-lg">🏦</span>
                      <span>İpoteka var</span>
                    </span>
                  )}
                  {listing.cixaris && (
                    <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <span className="text-lg">📄</span>
                      <span>Çıxarış (Kupça) var</span>
                    </span>
                  )}
                </div>
              )}
              <p className="text-3xl font-bold text-[#B91C1C] mb-3 logo-text">{formatPrice(listing.price, listing.category)}</p>
              <div className="flex items-center gap-2 text-gray-700">
                <MapPin size={18} className="text-[#B91C1C]" />
                <button
                  onClick={handleMapClick}
                  className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  {listing.location}
                </button>
              </div>
            </div>

            {/* Details */}
            <div className="bg-white rounded-xl p-6 border">
              <h2 className="text-lg font-semibold mb-4">Əmlak məlumatları</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-gray-900">{listing.type}</p>
                  <p className="text-sm text-gray-500">Növ</p>
                </div>
                {listing.rooms > 0 && (
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-xl font-bold text-gray-900">{listing.rooms}</p>
                    <p className="text-sm text-gray-500">Otaq</p>
                  </div>
                )}
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-gray-900">{listing.area}</p>
                  <p className="text-sm text-gray-500">m²</p>
                </div>
                {listing.floor > 0 && (
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-xl font-bold text-gray-900">{listing.floor}/{listing.totalFloors}</p>
                    <p className="text-sm text-gray-500">Mərtəbə</p>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white rounded-xl p-6 border" id="description-section">
              <h2 className="text-lg font-semibold mb-4">Son Məlumat (Təsvir)</h2>
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{listing.description || 'Təsvir mövcud deyil.'}</p>
            </div>

            {/* Video Section */}
            {listing.video_url && (
              <div className="bg-white rounded-xl p-6 border">
                <h2 className="text-lg font-semibold mb-4">Video</h2>
                <div className="aspect-video rounded-lg overflow-hidden">
                  {listing.video_url.includes('youtube.com') || listing.video_url.includes('youtu.be') ? (
                    <iframe
                      src={listing.video_url.includes('embed') ? listing.video_url : `https://www.youtube.com/embed/${listing.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1]}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={listing.video_url}
                      controls
                      className="w-full h-full"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Similar Listings Section - Geniş Siyahı */}
            {similarListings.length > 0 && (
              <div className="bg-white rounded-xl p-6 border">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 logo-text">Bənzər Elanlar ({similarListings.length})</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {similarListings.map((similarListing) => (
                    <ListingCard
                      key={similarListing.id}
                      listing={similarListing}
                      isFavorite={favorites.includes(similarListing.id)}
                      onFavoriteToggle={handleFavoriteToggle}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Sticky Əlaqə Bölməsi */}
          <div className="space-y-6 relative">
            <div 
              ref={contactRef}
              className="bg-white rounded-xl p-6 border shadow-lg transition-all relative"
            >
              <h3 className="text-xl font-bold mb-6 text-gray-900">Əlaqə</h3>
              {agency && (
                <Link to={`/agencies/${agency.id}`} className="flex items-center gap-3 mb-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl hover:from-red-100 hover:to-pink-100 border border-red-100 transition-colors">
                  <div className="w-12 h-12 bg-[#B91C1C] rounded-full flex items-center justify-center flex-shrink-0">
                    <Building2 size={24} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{agency.name}</p>
                    <p className="text-sm text-gray-600">Agentlik</p>
                  </div>
                </Link>
              )}
              {listing.ownerType === 'sahibinden' && (
                <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                  <p className="font-semibold text-green-700">Sahibindən</p>
                  <p className="text-sm text-green-600 mt-1">Birbaşa mülk sahibindən</p>
                </div>
              )}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-600 mb-2">Telefon Nömrəsi</p>
                <p className="text-2xl font-bold text-gray-900">{listing.phone}</p>
              </div>
              <div className="space-y-3">
                <Button 
                  onClick={handleCall} 
                  className="w-full h-14 bg-gradient-to-r from-[#B91C1C] to-[#991B1B] hover:from-[#991B1B] hover:to-[#7F1D1D] text-white text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  <Phone size={20} className="mr-2" />Zəng et
                </Button>
                <Button 
                  onClick={handleWhatsApp} 
                  variant="outline" 
                  className="w-full h-14 border-2 border-green-500 text-green-600 hover:bg-green-50 hover:border-green-600 text-base font-semibold shadow-md hover:shadow-lg transition-all"
                >
                  <MessageCircle size={20} className="mr-2" />WhatsApp
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
